import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonIcon, IonImg, IonLabel,
  IonText,
} from '@ionic/react';
import classNames from 'classnames';
import { closeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Minus from '@/assets/icons/managed/simulator_minus.webp';
import Plus from '@/assets/icons/managed/simulator_plus.webp';
import PepperLogo from '@/assets/imgs/pepper-logo.svg';
import { getSimulations } from '@/redux/eDniModal/eDniModalAPI';
import { CommercialOfferType, LoanSimulation } from '@/redux/eDniModal/eDniModalInterface';
import { saveBudgetSimulations, saveIsPreferredOffer, saveSimulationTerm } from '@/redux/orderManaged/orderManaged';
import { TermsType } from '@/redux/orderManaged/orderManagedInterface';
import { RootState } from '@/store';
import { formatPercentage, pricesHelper, renderOpeningFee } from '@/utils/pricesHelper';

import styles from './ModalSimulation.module.scss';
import { checkpreferredOfferAndNoPreferredOffer } from '@/redux/commercialOffers/commercialOffers';

interface ModalSimulationProps {
  currentPrice: number;
  onDismiss: () => void;
}

export default function ModalSimulation({ currentPrice, onDismiss }: ModalSimulationProps) {
  const dispatch = useDispatch();
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [termCount, setTermCount] = useState<TermsType | undefined>(undefined);
  const [offerSelected, setOfferSelected] = useState<LoanSimulation>();
  const [displayInfo, setDisplayInfo] = useState<boolean>(false);

  const termAlreadySaved = useSelector((state: RootState) => state.orderData.simulationTerm);
  const offers = useSelector((state: RootState) => state.orderData.simulationsManaged);

  const selectTermScreen = (terms?: number) => {
    if (terms) {
      if (terms > 5) {
        return TermsType.MANY_TERMS;
      }
      if (terms === 1) {
        return TermsType.ONLY_TERM;
      }
      if (terms > 1 && terms < 6) {
        return TermsType.FEW_TERMS;
      }
    }
    return undefined;
  };

  const selectFinancialOffer = async (offers: LoanSimulation[]) => {
    if (termAlreadySaved) {
      const termOffer = offers.find((offer: LoanSimulation) => offer.term === termAlreadySaved);
      if (termOffer) {
        setOfferSelected(termOffer);
      } else {
        setOfferSelected(offers[offers.length - 1]);
      }
    } else {
      setOfferSelected(offers[offers.length - 1]);
    }
    setTermCount(selectTermScreen(offers.length));
    setisLoading(false);
  };

  const updateSimulation = async (amount: number) => {
    const sim = {
      amount,
      flows: ['ECO'],
    };
    const simulations = (await getSimulations(sim)).data;
    let budgetOffers: any[] = [];
    if (simulations.commercial_offers.length > 0) {
      simulations.commercial_offers.map((offers: CommercialOfferType) => {
        budgetOffers.push(...offers.financial_offers);

      });
      //coge el valor de is_preferrred
      budgetOffers.sort((a, b) => a.term - b.term);
    } else {
      budgetOffers = simulations.commercial_offers[0].financial_offers;
    }
    const yespreferredOffer = simulations.commercial_offers.some(
      (commercialOffer: CommercialOfferType) => commercialOffer.is_preferred
    );
    const noPreferredOffer = simulations.commercial_offers.some(
      (commercialOffer: CommercialOfferType) => !commercialOffer.is_preferred
    );

    dispatch(checkpreferredOfferAndNoPreferredOffer(yespreferredOffer && noPreferredOffer));
    dispatch(saveIsPreferredOffer(simulations.commercial_offers.some(
      (commercialOffer: CommercialOfferType) => commercialOffer.is_preferred
    )));
    dispatch(saveBudgetSimulations(budgetOffers));
    selectFinancialOffer(budgetOffers);
    setTermCount(selectTermScreen(budgetOffers.length));
    setisLoading(false);
  };

  const selectOffer = async (term: number) => {
    const valueFound = offers?.find(
      (financialOffer: LoanSimulation) => financialOffer.term === term,
    );
    if (valueFound) {
      setOfferSelected(valueFound);
    }
  };

  const isDecreaseButtonActive = () => {
    return offers
      && offerSelected?.term !== offers[0].term
      && termCount !== TermsType.ONLY_TERM;
  }

  const isIncreaseButtonActive = () => {
    return offers
      && offerSelected?.term !== offers[offers.length - 1].term
      && termCount !== TermsType.ONLY_TERM;
  }

  const increaseTerm = () => {
    if (offers && offerSelected) {
      const currentIndex = offers.findIndex((offer: LoanSimulation) => offer.term === offerSelected.term);
      if (currentIndex < offers.length - 1) {
        setOfferSelected(offers[currentIndex + 1]);
      }
    }
  };

  const decreaseTerm = () => {
    if (offers && offerSelected) {
      const currentIndex = offers.findIndex((offer: LoanSimulation) => offer.term === offerSelected.term);
      if (currentIndex && currentIndex > 0) {
        setOfferSelected(offers[currentIndex - 1]);
      }
    }
  };

  const handleContinue = () => {
    if (offerSelected) {
      dispatch(saveSimulationTerm(offerSelected?.term)); 
    }
    onDismiss();
  };

  useEffect(() => {
    if (!offers) {
      setisLoading(true);
      updateSimulation(currentPrice);
    } else {
      selectFinancialOffer(offers);
      selectTermScreen(offers.length);
    }
  }, []);

  return (
    <div
      className={styles.modalContainerSimulation}
    >
      {(isLoading || termCount === undefined) ? (
        <div className={styles.modalSimulationLoading}>
          <img
            src={PepperLogo}
            alt="Pepper Logo"
            className={styles.pulseLogo}
          />
        </div>
      )
        : (
          <div className={styles.miniModal}>
            <div className={styles.miniModalHeader}>
              <IonLabel className={styles.miniModalTitle}>Simular financiación</IonLabel>
              <IonIcon
                icon={closeOutline}
                className={styles.miniModalCloseIcon}
                onClick={onDismiss}
              />
            </div>
            <div className={styles.miniModalContent}>
              {termCount === TermsType.FEW_TERMS
                ? (
                  <div className={styles.miniModalItems}>
                    {offers?.map((offer: LoanSimulation) => (
                      <div
                        key={offers.indexOf(offer)}
                        className={classNames({
                          [styles.modalItemOptionSelected]: offerSelected === offer,
                          [styles.modalItemOption]: offerSelected !== offer,
                        })}
                        onClick={() => selectOffer(offer.term)}
                      >
                        <div className={styles.modalItemOptionLeft}>
                          <IonCheckbox
                            className={classNames({
                              [styles.simulationCheckboxChecked]: offerSelected?.term === offer.term,
                              [styles.simulationCheckbox]: offerSelected?.term !== offer.term,
                            })}
                            checked={offerSelected?.term === offer.term}
                            labelPlacement="end"
                          />
                          <IonLabel className={styles.modalItemDynamicData}>
                            {offer.term}
                          </IonLabel>
                          {' '}
                          <IonLabel className={styles.modalItemText}>cuotas</IonLabel>
                        </div>
                        <div>
                          <IonLabel className={styles.modalItemDynamicData}>
                            {pricesHelper(offer.regular_fee)}
                          </IonLabel>
                          {' '}
                          <IonLabel className={styles.modalItemText}>/mes</IonLabel>
                        </div>
                      </div>
                    ))}
                  </div>
                )
                : (
                  <div className={styles.miniModalSimulator}>
                    <div className={styles.simulatorRow}>
                      <IonImg
                        src={Minus}
                        alt="simulator-decrease-btn"
                        className={classNames({
                          [styles.simulatorIcon]: isDecreaseButtonActive(),
                          [styles.simulatorIconDisabled]: !isDecreaseButtonActive(),
                        })}
                        onClick={() => isDecreaseButtonActive() && decreaseTerm()}
                      />
                      <IonText className={styles.simulatorFee}>{offerSelected?.term}</IonText>
                      <IonImg
                        src={Plus}
                        alt="simulator-decrease-btn"
                        className={classNames({
                          [styles.simulatorIcon]: isIncreaseButtonActive(),
                          [styles.simulatorIconDisabled]: !isIncreaseButtonActive(),
                        })}
                        onClick={() => isIncreaseButtonActive() && increaseTerm()}
                      />
                    </div>
                    <div className={styles.modalFeeBox}>
                      <div className={styles.simulationFirstFeeRow}>
                        <div className={styles.firstFeeText}>
                          <IonLabel className={styles.firstFeeTodayLabel}>Hoy</IonLabel>
                          <IonText className={styles.firstFeeTitle}>Primer pago</IonText>
                        </div>
                        <div>
                          <IonText className={styles.modalFirstFeeAmount}>{offerSelected && pricesHelper(offerSelected?.first_fee)}</IonText>
                        </div>
                      </div>
                      <div className={classNames(styles.simulationBoxRow, styles.simulationRegularFeeRow)}>
                        <IonText className={styles.regularFeeTitle}>
                          Cuotas restantes
                          (
                          {offerSelected && offerSelected?.term - 1}
                          )
                        </IonText>
                        <IonText className={styles.regularFeeAmount}>
                          {offerSelected && pricesHelper(offerSelected?.regular_fee)}
                          {' '}
                          / mes
                        </IonText>
                      </div>
                    </div>
                  </div>
                )}
              <div className={styles.modalFinanceDetail}>
                <div className={classNames({
                  [styles.modalFinanceBoxHeaderItems]: termCount === TermsType.FEW_TERMS,
                  [styles.modalFinanceBoxHeaderSimulator]: termCount === TermsType.MANY_TERMS || termCount === TermsType.ONLY_TERM,
                })}
                >
                  <IonText className={styles.modalFinanceDetailTitle}>
                    Detalle de la financiación
                  </IonText>
                  <IonText className={styles.modalFinanceDetailInfo} onClick={() => setDisplayInfo(!displayInfo)}>+ Info</IonText>
                </div>
                <div className={styles.modalFinanceBox}>
                  <div>
                    <div className={styles.modalFinanceBoxAmountRow}>
                      <div className={styles.modalFinanceBoxAmountTitle}>
                        Importe de la compra
                      </div>
                      <div className={styles.modalFinanceBoxAmount}>
                        {offerSelected && pricesHelper(currentPrice)}
                      </div>
                    </div>
                    <div className={styles.modalFinanceBoxAdditionalData}>
                      TIN
                      {' '}
                      {offerSelected && formatPercentage(offerSelected?.tin, true)}
                      {' '}
                      |
                      {' '}
                      TAE
                      {' '}
                      {offerSelected && formatPercentage(offerSelected?.tae, true)}
                      {' '}
                      |
                      CA
                      {' '}
                      {renderOpeningFee(
                        offerSelected?.opening_fee ? offerSelected?.opening_fee : 0,
                        offerSelected?.opening_fee_type ? offerSelected?.opening_fee_type : '',
                      )}
                    </div>
                  </div>
                  {displayInfo
                    && (
                      <div>
                        <div className={styles.modalFinanceBoxAmountRow}>
                          <div className={styles.modalFinanceBoxAmountTitle}>
                            Importe total adeudado
                          </div>
                          <div className={styles.modalFinanceBoxAmount}>
                            {offerSelected && pricesHelper(offerSelected?.total_cost)}
                          </div>
                        </div>
                        <div className={styles.modalFinanceBoxAdditionalData}>
                          Financiación ofrecida por Pepper Finance Corporation, S.L.U y sujeta a su aprobación
                        </div>
                      </div>
                    )}
                </div>
              </div>
              <IonButtons className={classNames(styles.nextBtnWrapper, styles.simulationBtnWrapper)}>
                <IonButton
                  className="nextButton"
                  onClick={handleContinue}
                >
                  Continuar
                </IonButton>
              </IonButtons>
            </div>
          </div>
        )}
    </div>
  );
}
